import type {ComputedRef} from 'vue';
import {computed, ref} from 'vue';
import {useTranslation} from '#imports';
import type {RatedSkill} from '@api';
import type {
    IUpdateRatedSkill,
    IUseSkillRatingsField,
    NormalizedSkillRating,
} from '~/components/admin-section-tabs/qualification/types/IUseSkillRatingsField';

export function useSkillRatingsField(skillRatings: ComputedRef<RatedSkill[]>): IUseSkillRatingsField {
    const {t} = useTranslation();
    const isFullView = ref(false);

    const skillDisplayed = computed((): NormalizedSkillRating[] => {
        if (isFullView.value) {
            return skillRatings.value.map((it): NormalizedSkillRating => normalizedSkillRating(it));
        }
        const slicedSkills = skillRatings.value.slice(0, 2);
        return slicedSkills.map((skill: RatedSkill) => normalizedSkillRating(skill));
    });

    function normalizedSkillRating(skill: RatedSkill): NormalizedSkillRating {
        return {
            id: skill.tagHumanId ?? skill.label,
            label: skill.label,
            rating: skill.rating || 0,
        };
    }

    function updateRating(skillRating: NormalizedSkillRating, newRating: number | null): IUpdateRatedSkill {
        return skillRatings.value.map((it) => {
            if (skillRating.id === it.tagHumanId || skillRating.id === it.label) {
                return {
                    label: it.label,
                    rating: newRating,
                    tagHumanId: it.tagHumanId,
                };
            } else {
                return it;
            }
        });
    }

    function handleToggleFullView() {
        isFullView.value = !isFullView.value;
    }

    const getCTAText = computed(() => t(`profile.vetting.drawer.qualification.field.${isFullView.value ? 'less' : 'more'}`));

    const isCTAMoreVisible = computed((): boolean => skillRatings?.value.length > 2);

    return {
        isCTAMoreVisible,
        getCTAText,
        handleToggleFullView,
        updateRating,
        skillDisplayed,
    };
}
